import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import LanguageButton from 'components/button/language_button';
import InPlaceSelect from 'components/form/in_place_select';
import ReorderAcceptItem from 'components/exercise/reorder_accept_item';
import { capitalizeFirstLetter, isBlank, isPresent , pixify, underlineWronglyPositionedCharacters } from '@seedlang/utils';
import styled from '@emotion/styled';
import { Theme , Constants } from '@seedlang/constants';
import { AppUI, ExerciseUI } from '@seedlang/state';
import SpecialCharacterButtons from 'components/special_character_buttons';
import Text from 'components/text';
import { orderBy, shuffle, isString } from 'lodash';

const Wrapper = styled.div`

`;

const SentenceWrapper = styled.div`
  line-height: ${props => props.lineHeight};
  margin: 5px 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: ${props => props.justifyContent};
  flex-wrap: wrap;
  min-height: ${props => props.minHeight || 'auto'};
  input:focus {
    border: none;
    border-bottom: 1px solid black;
  }
  input {
    background: white;
    height: 40px;
    width: 100px;
    font-size: 18px;
    line-height: 15px;
    padding: 5px;
    flex: 1;
    border-radius: 0;
    border: none;
    border-bottom: 1px solid black;
  }
  select {
    height: 40px;
  }
  .in-place-select {
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const WordWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  margin: ${props => props.margin || '0 5px 0 0'};
  input, select {
    background: ${props => props.inputBackground};
    color: ${props => props.inputBackground ? '#FFF' : '#000'};
  }
`;

const LanguageButtonWrapper = styled.div`
    height: 40px;
    width: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const Punctuation = styled.div`
  font-size: 18px;
  line-height: 18px;
  margin: 0 1px;
  margin-right: ${props => props.attachToFollowingWord ? 0 : '1px'};
`;

const Revealed = styled.div`
  background: ${props => props.correct ? Theme.green : Theme.red};
  color: white;
  min-width: 100px;
  padding: 0 5px;
`;

const HighlightedWord = styled.div`
  padding: 0 5px;
  cursor: pointer;
  background: ${props => props.background};
  color: ${props => props.color};
  &:hover {
    background: ${props => props.submitted ? '#f9f91b' : '#e6e6e6'};
  }
`;

const NotSelected = styled.div`
  height: ${props => props.height}!important;
  display: flex;
  align-items: center;
`;

@observer
class ExerciseSentence extends React.Component {
  @observable showSource = false;

  @computed get languageId() {
    return this.showSource ? this.props.sentence.sourceTextForUserLanguageId(AppUI.user) : this.props.sentence.languageId;
  }
  @observable currentInputWordAssociation;
  @observable focusedWordAssociationId;
  @observable justFocused = false;
  @observable initialHeight = null;
  sentenceWrapperRef = React.createRef();

  componentDidUpdate(prevProps) {
    if (this.props.isFocused && !prevProps.isFocused) {
      this.focusedWordAssociationId = null;
    }
    if (prevProps.sentence?.id !== this.props.sentence?.id) {
      this.initialHeight = null;
    }
    if (
      prevProps.showOptionsInAlphabeticalOrder !== this.props.showOptionsInAlphabeticalOrder ||
      prevProps.optionsDisplayed !== this.props.optionsDisplayed ||
      prevProps.options !== this.props.options
    ) {
      this.optionsCache.clear();
    }
    this.focusOnFirstSelectedWordAssociation();
    this.captureInitialHeight();
  }

  componentDidMount() {
    this.focusOnFirstSelectedWordAssociation();
    this.captureInitialHeight();
  }
  captureInitialHeight = () => {
    if (this.initialHeight || !this.sentenceWrapperRef.current) { return; }
    const hasSelectedWordAssociations = this.wordAssociations?.some(item => item.selected);
    const hasUnfilledBlanks = hasSelectedWordAssociations && 
      this.wordAssociations.some(item => item.selected && !item.state.submittedValue);
    
    if (hasUnfilledBlanks || (hasSelectedWordAssociations && !this.props.revealed)) {
      setTimeout(() => {
        if (this.sentenceWrapperRef.current && !this.initialHeight) {
          const height = this.sentenceWrapperRef.current.offsetHeight;
          if (height > 0) {
            this.initialHeight = `${height}px`;
          }
        }
      }, 0);
    }
  }
  focusOnFirstSelectedWordAssociation = () => {
    if (this.props.inputType === 'text' && isBlank(this.focusedWordAssociationId) && this.props.isFocused && this.props.sentence.selectedWordAssociations.length > 0) {
      this.focusedWordAssociationId = this.props.sentence.selectedWordAssociations[0].id;
      const inputElement = document.getElementById(this.focusedWordAssociationId);
      if (inputElement) {
        inputElement.focus();
        this.justFocused = true;
        setTimeout(() => {
          this.justFocused = false;
        }, 100);
      }
    }
  }

  @computed get wordAssociations() {
    if (isPresent(this.props.sentence.wordAssociations)) {
      const reverseText = (AppUI.layout.isNative && AppUI.user.mobileTextDirection === 'reverse') || (!AppUI.layout.isNative && this.props.sentence?.rightToLeftLanguage) || (AppUI.layout.isNative && this.props.sentence?.rightToLeftLanguage && AppUI.user.mobileTextDirection !== 'forward');
      return reverseText ? this.props.sentence.wordAssociations.slice().reverse() : this.props.sentence.wordAssociations;
    }
    return null;
  }
  insertText = (text) => {
    if (isBlank(this.currentInputWordAssociation)) { return null; }
    const el = document.getElementById(this.currentInputWordAssociation.id);
    if (!el) { return null; }
    const caretPosition = el.selectionStart;
    el.focus();
    const newValue = [this.currentInputWordAssociation.state.submittedValue?.slice(0, caretPosition), text, this.currentInputWordAssociation.state.submittedValue?.slice(caretPosition)].join('');
    this.currentInputWordAssociation.state.set('submittedValue', newValue);
  }

  clickHighlight(item) {
    item.state.set('submittedValue', !item.state.submittedValue);
  }

  inputBackground(wordAssociation) {
    if (!this.props.revealed) { return null; }
    return wordAssociation.state.correct ? Theme.green : Theme.red;
  }
  onFocus = (wordAssociation) => {
    this.currentInputWordAssociation = wordAssociation;
    this.props.onFocus();
  }
  onKeyUpTextInput = (e) => {
    if (e.keyCode === 13 && this.props.onEnter && !this.justFocused) {
      this.props.onEnter();
    }
  }
  _capitalize = (index) => {
    return (index === 0 || ['.', '?', '!'].includes(this.wordAssociations[index - 1]?.punctuation)) && (!ExerciseUI.hasExercise || ExerciseUI.exercise.capitalizeEntries);
  }
  _revealedText = (item) => {
    if (item.state.correct
      && item.state.entryHasUserInput
      && item.adjustedTargetTextForExercise((ExerciseUI.exercise)).trim()?.length === item.state.submittedValue.trim()?.length
      && (item.state.incorrectCasing || item.state.incorrectSpecialCharacters || item.state.incorrectPunctuation)) {
      return underlineWronglyPositionedCharacters(
        item.adjustedTargetTextForExercise((ExerciseUI.exercise)),
        item.state.submittedValue,
      );
    } if (item.state.correct
      && !item.state.submittedValueMatchesAlternateText()) {
      return item.adjustedTargetTextForExercise(ExerciseUI.exercise);
    }
    return item.state.submittedValue;
  }
  highlightedWordBackground = (item) => {
    if (item.state.submittedValue && !this.props.revealed) {
      return '#f9f91b';
    } if (item.state.submittedValue && item.selected) {
      return Theme.green;
    } if (item.state.submittedValue) {
      return Theme.red;
    }
    return '#FFF';
  }
  highlightedWordColor = (item) => {
    if (this.props.revealed && item.state.submittedValue) {
      return '#FFF';
    }
  }

  @observable optionsCache = new Map();
  optionsForBlank = (wordAssociation) => {
    if (this.props.optionsDisplayed > 1) {
      // Use cached options if they exist for this word association
      if (!this.optionsCache.has(wordAssociation.id)) {
        const correctOptionsFirst = orderBy(this.props.options, targetText => {
          const isMatch = wordAssociation.targetText === targetText;
          return isMatch ? 0 : 1;
        });

        let finalOptions;
        if (this.props.showOptionsInAlphabeticalOrder) {
          const wrongSorted = orderBy(correctOptionsFirst.slice(1), t => String(t).toLowerCase());
          finalOptions = [
            correctOptionsFirst[0],
            ...wrongSorted.slice(0, this.props.optionsDisplayed - 1),
          ];
          finalOptions = orderBy(finalOptions, t => String(t).toLowerCase());
        } else {
          const shuffledOptions = shuffle(correctOptionsFirst.slice(1));
          finalOptions = [correctOptionsFirst[0], ...shuffledOptions.slice(0, this.props.optionsDisplayed - 1)];
          finalOptions = orderBy(finalOptions, targetText => targetText);
        }

        this.optionsCache.set(wordAssociation.id, finalOptions);
      }

      return this.optionsCache.get(wordAssociation.id);
    }
    return this.props.options;
  }

  selectOptionsForBlank = (wordAssociation) => {
    const options = this.optionsForBlank(wordAssociation);
    const capitalize = wordAssociation.firstInSentence &&
      (!ExerciseUI.hasExercise || ExerciseUI.exercise.capitalizeEntries);
    if (!capitalize || isBlank(options)) {
      return options;
    }

    const firstOption = options[0];
    if (isString(firstOption)) {
      return options.map((text) => {
        const label = capitalizeFirstLetter(text);
        return [text, label ?? text];
      });
    }

    return options.map((opt) => {
      const label = capitalizeFirstLetter(opt.targetText);
      return [opt.id, label ?? opt.targetText];
    });
  };

  render() {
    return (
      <Wrapper
        lineHeight={this.props.inDeck ? '50px' : '30px'}
      >
        <SentenceWrapper
          ref={this.sentenceWrapperRef}
          justifyContent={this.props.justifyContent}
          minHeight={this.initialHeight}
        >
          {
            !this.props.hideLanguageButton && this.props.sentence.sourceTextForUser(AppUI.user) &&
              <LanguageButtonWrapper>
                <LanguageButton
                  languageId={this.languageId}
                  onClick={() => this.showSource = !this.showSource}
                />
              </LanguageButtonWrapper>
          }
          { this.showSource && this.props.sentence.sourceTextForUser(AppUI.user) }
          { isPresent(this.props.sentence) && !this.showSource && (this.wordAssociations?.length === 0 || this.props.showTargetText) && this.props.sentence.targetText}
          {
            isPresent(this.props.sentence) && !this.showSource && !this.props.showTargetText && this.wordAssociations?.length > 0 && this.wordAssociations.map((item, index) => {
              return (
                <WordWrapper
                  key={item.id}
                  margin={this.props.wordMargin}
                  inputBackground={this.inputBackground(item)}
                >
                  {
                    (item.precedingPunctuation || (item.punctuation && this.props.sentence?.rightToLeftLanguage)) &&
                      <Punctuation attachToFollowingWord={['¿', '¡'].some(mark => item.precedingPunctuation?.includes(mark))}>
                        {item.punctuation && this.props.sentence?.rightToLeftLanguage ? item.punctuation : item.precedingPunctuation}
                      </Punctuation>
                  }
                  {
                    item.selected && !this.props.revealed && this.props.inputType === 'text' &&
                      <input
                        type='text'
                        id={item.id}
                        value={item.state.submittedValue || ''}
                        onChange={e => item.state.set('submittedValue', e.currentTarget.value)}
                        onKeyUp={this.onKeyUpTextInput}
                        onFocus={() => this.onFocus(item)}
                        autoComplete='off'
                        style={{
                          width: '100%',
                          maxWidth: pixify(Math.max(100, (item.targetTextFromWordAssociation || item.word.targetText)?.length * 15)),
                        }}
                      />
                  }
                  {
                    item.selected && this.props.inputType === 'select' &&
                      <InPlaceSelect
                        allowUpdate
                        includeBlank
                        value={item.state.submittedValue || ''}
                        options={this.selectOptionsForBlank(item)}
                        disabled={this.props.revealed}
                        onChange={val => item.state.set('submittedValue', val)}
                      />
                  }
                  {
                    item.selected && this.props.inputType === 'reorder' &&
                      <ReorderAcceptItem
                        height={this.props.inDeck ? '50px' : '40px'}
                        correct={this.props.revealed && item.state.correct}
                        incorrect={this.props.revealed && !item.state.correct}
                        disabled={this.props.revealed}
                        capitalize={this._capitalize(index)}
                        value={item.state.submittedValue || ''}
                        onClick={() => ExerciseUI.clearWordAssociation(item, this.props.sentence)}
                        fontSize={Constants.RTL_LANGUAGE_IDS.includes(this.languageId) ? '16px' : '14px'}
                        id={item.id}
                        minWidth={ExerciseUI.layout.isMobile && !this.props.inDeck ? '60px' : '100px'}
                        inDeck={this.props.inDeck}
                      />
                  }
                  {
                    item.selected && this.props.revealed && this.props.inputType === 'text' &&
                      <Revealed
                        correct={item.state.correct}
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: this._revealedText(item) }}
                        />
                      </Revealed>
                  }
                  {
                    this.props.inputType === 'highlight' &&
                      <HighlightedWord
                        submitted={item.state.submittedValue}
                        onClick={() => this.clickHighlight(item, index)}
                        background={this.highlightedWordBackground(item)}
                        color={this.highlightedWordColor(item)}
                      >
                        {index === 0 && this.props.firstWordIsLowercase && !ExerciseUI.exercise?.capitalizeEntries ? item.targetText : item.adjustedTargetTextForExercise((ExerciseUI.exercise))}
                      </HighlightedWord>
                  }
                  {
                    !item.selected && this.props.inputType !== 'highlight' &&
                      <NotSelected
                        className='not-selected'
                        height={this.props.inDeck ? '50px' : '40px'}
                      >
                        {this.props.sentence.simpleWord && AppUI.siteIsSeedlang ? item.targetText : item.adjustedTargetTextForExercise(ExerciseUI.exercise)}
                      </NotSelected>
                  }
                  {
                    item.punctuation && !this.props.sentence.rightToLeftLanguage &&
                      <Punctuation>
                        {item.punctuation}
                      </Punctuation>
                  }
                </WordWrapper>
              );})
          }
        </SentenceWrapper>
        {
          this.props.isFocused &&
            <SpecialCharacterButtons
              margin='10px 0 0 0'
              showCloseButton
              languageId={ExerciseUI.groupId === Constants.EASY_GERMAN_GROUP_ID ? 'DE' : null}
              onClick={this.insertText}
            />
        }
        {
          this.props.revealed && this.props.showAlternateTextFeedback &&
            <Text
              fontSize='14px'
              style={{fontWeight: 'normal'}}
              color={Theme.green}
              margin='10px 0 0 0'
            >
              Another possible answer is: <i>{this.props.sentence.targetText}</i>
            </Text>
        }
      </Wrapper>
    );
  }
}

export default ExerciseSentence;
